<template>
  <div
    class="blockquote"
    :class="{
      'blockquote--partners': content === 'partners',
      container: content === 'partners',
    }"
  >
    <div class="blockquote-slider" aria-live="polite">
      <div
        v-for="(item, index) in items"
        :id="`slide-${index}`"
        :key="item.author"
        class="blockquote-slider__item"
        :class="index === 0 ? 'active' : ''"
        :slide-order="index"
      >
        <blockquote class="blockquote-slider__text">
          {{ item.text }}
        </blockquote>
      </div>
    </div>
    <div class="blockquote-people">
      <div class="blockquote-people-slider__mobile-controls">
        <button
          v-for="(item, index) in items"
          :key="item.author"
          class="blockquote-people-slider__mobile-button"
          :class="index === 0 ? 'active' : ''"
          :slide-order="index"
          :aria-controls="`slide-${index}`"
          :aria-label="item.author"
        ></button>
      </div>
      <div class="blockquote-people-slider">
        <div
          v-for="(item, index) in items"
          :id="`control-${index}`"
          :key="item.author"
          class="blockquote-people-slider__item"
          :class="index === 0 ? 'active' : ''"
          :slide-order="index"
        >
          <button
            :id="getButtonID(item.author)"
            class="blockquote-people-slider__button"
            :aria-controls="`slide-${index}`"
            :aria-label="item.author"
            :slide-order="index"
          ></button>
          <div class="blockquote-people-slider__box" aria-hidden="true">
            <div class="blockquote-people-slider__img-box">
              <img
                class="blockquote-people-slider__img"
                :src="itemImage(item)"
                alt=""
              />
            </div>
            <div class="blockquote-people-slider__author">
              <p class="blockquote-people-slider__name">
                {{ item.author }}
              </p>
              <p
                v-if="item.subtitle"
                class="blockquote-people-slider__subtitle"
              >
                {{ item.subtitle }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { TestimonialRecord, DATA } from '@/pages-site-v2/helpers/testimonials'

const props = defineProps<{
  content: keyof typeof DATA
}>()

const items = computed(() => {
  return DATA[props.content]
})

function getButtonID(authorName: string): string {
  const firstName = authorName.split(' ')[0].toLowerCase()
  return `review_${firstName}`
}

function itemImage(item: TestimonialRecord): string {
  // See https://stackoverflow.com/q/40491506/4612064
  // The 'require' here allows to use dynamic image path
  return require(`@/assets.v2/images/${item.image}`)
}
</script>

<style lang="scss" scoped>
@import '@/assets.v2/css/custom/variables';

.blockquote {
  --slide-speed: 0.6s;
}

.blockquote-slider {
  text-align: center;
  max-width: 860px;
  margin-inline: auto;
  padding-top: 50px;
  padding-bottom: 80px;
  display: flex;
  overflow: hidden;
  @media (max-width: $md-up) {
    padding-top: 70px;
    padding-bottom: 86px;
  }
  @media (max-width: $sm-up) {
    padding-top: 30px;
    padding-bottom: 15px;
  }
  &__text {
    position: relative;
    font-size: 24px;
    line-height: 1.67;
    letter-spacing: -0.01em;
    color: $c-dark-black;
    @media (max-width: $sm-up) {
      font-size: 16px;
      line-height: 30px;
      letter-spacing: -0.02em;
    }
    &:before {
      content: '';
      display: block;
      width: 118px;
      height: 83px;
      background: url(@/assets.v2/images/quote.svg) center no-repeat;
      position: absolute;
      top: -30px;
      left: 0;
      right: 0;
      margin: auto;
      z-index: -1;
      @media (max-width: $sm-up) {
        width: 90px;
        height: 63px;
      }
    }
  }
  &__item {
    flex-shrink: 0;
    width: 100%;
    visibility: hidden;
    opacity: 0;
    animation: fadeOut calc(var(--slide-speed) / 2) ease-in-out;
    &.active {
      visibility: visible;
      opacity: 1;
      order: -1;
      animation: fadeIn var(--slide-speed) ease-in-out;
    }

    @keyframes fadeIn {
      0%,
      50% {
        visibility: visible;
        opacity: 0;
        order: -1;
      }
      100% {
        opacity: 1;
      }
    }
    @keyframes fadeOut {
      0% {
        opacity: 1;
        order: -1;
        visibility: visible;
      }
      100% {
        opacity: 0;
        order: auto;
        visibility: hidden;
      }
    }
  }
}

.blockquote-people {
  max-width: 864px;
  margin: 0 auto 48px;
  border-top: 2px solid $c-border;
  @media (max-width: $sm-up) {
    border: 0;
    margin-bottom: 33px;
    overflow: hidden;
  }
}
.blockquote-people-slider {
  max-width: 734px;
  margin: 0 auto;
  position: relative;
  top: -2px;
  display: flex;
  @media (max-width: $md-up) {
    max-width: 630px;
  }
  @media (max-width: $sm-up) {
    top: 0;
    max-width: unset;
    transform: translateX(calc(100% * var(--current-slide-index) * -1));
    transition: transform var(--slide-speed);
  }
  &__mobile-controls {
    display: none;
    @media (max-width: $sm-up) {
      display: flex;
      justify-content: center;
      gap: 10px;
      width: 100%;
      margin-bottom: 15px;
    }
  }
  &__mobile-button {
    border: none;
    width: 27px;
    height: 3px;
    border-radius: 3px;
    background: $c-grey;

    &.active {
      background: $c-dark-black;
    }
  }
  &__item {
    width: 100%;
    position: relative;
    @media (max-width: $sm-up) {
      width: 100%;
      flex-shrink: 0;
    }
  }
  &__button {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    z-index: 1;
  }
  &__box {
    cursor: pointer;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 26px 20px 0;
    --transition: 0.2s ease;
    @media (max-width: $sm-up) {
      padding: 0;
    }
    &:before {
      content: '';
      display: block;
      width: 0;
      height: 4px;
      background-color: $c-dark-black;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      transition: all 0.6s;
      @media (max-width: $sm-up) {
        content: none;
      }
    }
  }
  &__img-box {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    margin-right: 10px;
    opacity: 0.6;
    transition: opacity var(--transition);
    @media (max-width: $md-up) {
      width: 41px;
      height: 41px;
      margin-right: 8px;
    }
    @media (max-width: $sm-up) {
      width: 33px;
      height: 33px;
      margin-right: 12px;
    }
  }
  &__name {
    font-size: 20px;
    line-height: 1;
    color: $c-scrollbar;
    text-align: start;
    transition: color var(--transition);
    @media (max-width: $md-up) {
      font-size: 17px;
    }
    @media (max-width: $sm-up) {
      font-size: 16px;
      color: $c-dark-black;
    }
  }
  &__subtitle {
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0;
    text-wrap: balance;
    color: $c-black-3;
    opacity: 0.5;
    transition: all var(--transition);
  }
  &__item {
    @include hover {
      .blockquote-people-slider__img-box {
        opacity: 1;
      }
      .blockquote-people-slider__name {
        color: $c-dark-black;
        font-weight: 500;
      }
      .blockquote-people-slider__subtitle {
        opacity: 1;
      }
    }
  }
  &__item.active {
    .blockquote-people-slider__img-box {
      opacity: 1;
    }
    .blockquote-people-slider__name {
      color: $c-dark-black;
      font-weight: 500;
    }
    .blockquote-people-slider__subtitle {
      opacity: 1;
    }
    .blockquote-people-slider__box {
      &:before {
        opacity: 1;
        width: 100%;
      }
    }
  }
}

.blockquote--partners {
  --slide-speed: 0.5s;
  max-width: 900px;
  margin-bottom: 95px;
  @media (max-width: $md) {
    max-width: var(--container-max-width);
    margin-bottom: 61px;
  }
  @media (max-width: $sm) {
    margin-bottom: 49px;
  }

  .blockquote-people {
    max-width: 100%;
    @media (max-width: $md) {
      max-width: var(--container-max-width);
      border: 0;
      margin-bottom: 33px;
      overflow: hidden;
    }
  }

  .blockquote-slider {
    position: relative;
    max-width: 100%;
    padding-top: 44px;
    padding-bottom: 48px;
    @media (max-width: $md) {
      padding-top: 32px;
      padding-bottom: 28px;
    }

    &:before {
      content: '';
      display: block;
      width: 114px;
      height: 80px;
      background: url(@/assets.v2/images/quote.svg) center no-repeat;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      margin: auto;
      z-index: -1;
      @media (max-width: $md) {
        width: 92px;
        height: 64px;
        background-size: contain;
      }
    }

    &__text {
      font-size: 20px;
      line-height: 32px;
      letter-spacing: -0.01em;
      @media (max-width: $md) {
        font-size: 18px;
        line-height: 32px;
      }
      @media (max-width: $sm) {
        font-size: 16px;
        line-height: 30px;
        letter-spacing: -0.02em;
      }

      &:before {
        display: none;
      }
    }
  }

  .blockquote-people-slider {
    width: 100%;
    max-width: none;

    @media (max-width: $md) {
      top: 0;
      max-width: unset;
      transform: translateX(calc(100% * var(--current-slide-index) * -1));
      transition: transform var(--slide-speed);

      &__mobile-controls {
        display: flex;
        justify-content: center;
        gap: 20px;
        width: 100%;
        margin-bottom: 24px;
      }

      &__mobile-button {
        width: 40px;
        height: 4px;
      }
    }

    &__box {
      padding-top: 20px;
      padding-inline: 24px;
      @media (max-width: $md) {
        max-width: 300px;
        padding-top: 0;
        margin-inline: auto;
      }

      &:before {
        left: 24px;
        width: calc(100% - 48px);
        border-radius: 99px;
        transform: scaleX(0);
        transform-origin: center;
        transition: all 0.3s ease;
        @media (max-width: $md) {
          content: none;
        }
      }
    }

    &__img-box {
      width: 56px;
      height: 56px;
      margin-right: 16px;
      @media (max-width: $md) {
        width: 52px;
        height: 52px;
      }
    }

    &__name {
      font-weight: 500;
      line-height: 29px;
      margin-bottom: 2px;
      @media (max-width: $md) {
        font-size: 20px;
        line-height: 29px;
        margin-bottom: 0;
      }
    }

    &__item {
      @media (max-width: $md) {
        width: 100%;
        flex-shrink: 0;
      }

      @include hover {
        .blockquote-people-slider__name {
          font-weight: 500;
        }
      }

      &.active {
        .blockquote-people-slider__name {
          font-weight: 500;
        }

        .blockquote-people-slider__box:before {
          width: calc(100% - 48px);
          transform: scaleX(1);
        }
      }
    }
  }
}
</style>
